/**
 * destinations-search-modal.js — Liquid-first architecture
 *
 * All country / region / popular-destination data is pre-rendered in the page
 * by Shopify Liquid. This script handles:
 *   - Modal open / close animation + mobile scroll lock
 *   - In-memory search filtering (zero network calls in the search path)
 *   - Price formatting from Liquid pre-rendered from_price values
 *   - Popular destinations from Liquid pre-rendered data
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────
  const TRANSITION_DURATION = 300;
  const FOCUS_DELAY = 100;
  const MOBILE_BREAKPOINT = 768;
  const OVERLAY_CLICK_DELAY = 50;
  // const FROM_PRICE_CURRENCY = 'AUD'; // price display commented out — VWO winning test

  function capitalizeWords(str) {
    return str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  document.querySelectorAll('[data-dsm]').forEach(initModal);

  function initModal(root) {
    // ── Parse pre-rendered data ────────────────────────────────
    const countriesEl = root.querySelector('[data-dsm-countries]');
    if (!countriesEl) {
      console.warn('DSM: No [data-dsm-countries] tag found.');
      return;
    }
    let countries = [],
      regions = [],
      popular = [],
      rankingsByCountry = {},
      unlimitedHandles = {};
    try {
      countries = JSON.parse(countriesEl.textContent) || [];
      const regionsEl = root.querySelector('[data-dsm-regions]');
      const popularEl = root.querySelector('[data-dsm-popular]');
      const rankingsEl = root.querySelector('[data-dsm-rankings]');
      if (regionsEl) regions = JSON.parse(regionsEl.textContent) || [];
      if (popularEl) popular = JSON.parse(popularEl.textContent) || [];
      if (rankingsEl) rankingsByCountry = JSON.parse(rankingsEl.textContent) || {};

      for (const key in rankingsByCountry) {
        if (key.startsWith('unlimited_handles_')) {
          const country = key.replace('unlimited_handles_', '');
          unlimitedHandles[country] = rankingsByCountry[key];
        }
      }
    } catch (e) {
      console.warn('DSM: Failed to parse pre-rendered data', e);
    }

    // ── DOM refs ───────────────────────────────────────────────
    const overlay = root.querySelector('.ds-modal-overlay');
    const dialog = root.querySelector('.ds-modal-dialog');
    const modalInput = root.querySelector('.dsearch__input--modal');
    const triggerInput = root.querySelector('.dsearch__input:not(.dsearch__input--modal)');
    const dropdown = root.querySelector('.dsearch__dropdown--modal');

    if (!overlay || !dialog || !modalInput) {
      console.warn('DSM: Required elements not found');
      return;
    }

    // ── State ──────────────────────────────────────────────────
    let isModalOpen = false;
    let bodyScrollY = 0;
    let lockedAppHeight = null;
    let touchMoveHandler = null;
    let overlayClickHandler = null;
    let escKeyHandler = null;
    let debounceTimer = null;

    // ── Event listeners ────────────────────────────────────────
    if (triggerInput) {
      triggerInput.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!isModalOpen) openModal();
      });
      triggerInput.addEventListener('focus', () => {
        if (!isModalOpen) {
          // data-track is added by vwo.js only for the VWO test variation,
          // so guard rather than always publishing.
          // added the analytics event to the focus listener here,
          // since the inline script in theme.liquid only fires on click events
          if (triggerInput.dataset.track && window.Shopify?.analytics?.publish) {
            const props = { ...triggerInput.dataset };
            delete props.track;
            props._urgent = true;
            Shopify.analytics.publish('track:' + triggerInput.dataset.track, props);
          }
          openModal();
        }
      });
    }

    modalInput.addEventListener('input', onInput);
    modalInput.addEventListener('focus', () => {
      if (!modalInput.value.trim()) showPopular();
    });
    modalInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });

    root.querySelectorAll('.ds-modal-close').forEach((btn) => {
      btn.addEventListener('click', closeModal);
    });

    escKeyHandler = (e) => {
      if (e.key === 'Escape' && isModalOpen) closeModal();
    };
    document.addEventListener('keydown', escKeyHandler);

    // ── Open modal ─────────────────────────────────────────────
    function openModal() {
      if (isModalOpen) return;
      if (triggerInput && document.activeElement === triggerInput) triggerInput.blur();

      isModalOpen = true;

      if (dialog.parentNode !== overlay) overlay.appendChild(dialog);
      if (overlay.parentNode !== document.body) document.body.appendChild(overlay);

      overlay.classList.remove('transition-complete');
      overlay.removeAttribute('hidden');
      dialog.removeAttribute('hidden');

      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

      // Lock viewport height on mobile before keyboard opens
      if (isMobile) {
        try {
          const h = window.innerHeight + 'px';
          lockedAppHeight = h;
          document.documentElement.style.setProperty('--modal-locked-height', h);
          overlay.classList.add('height-locked');
          dialog.classList.add('height-locked');
        } catch (e) {
          /* non-fatal */
        }
      }

      void overlay.offsetWidth; // force reflow so CSS transitions fire

      if (isMobile) {
        lockBodyScroll();
        bindTouchMoveLock();
      } else {
        document.body.style.overflow = 'hidden';
      }

      addOverlayClickHandler();

      // Focus input on all devices — height is locked before keyboard affects layout
      setTimeout(() => modalInput.focus(), FOCUS_DELAY);
      setTimeout(() => {
        if (!modalInput.value.trim()) showPopular();
      }, FOCUS_DELAY);
    }

    // ── Close modal ────────────────────────────────────────────
    function closeModal() {
      if (!isModalOpen) return;
      isModalOpen = false;

      removeOverlayClickHandler();
      overlay.classList.remove('transition-complete');
      overlay.setAttribute('hidden', '');
      dialog.setAttribute('hidden', '');

      setTimeout(() => {
        if (window.innerWidth < MOBILE_BREAKPOINT) {
          unlockBodyScroll();
          unbindTouchMoveLock();
        } else {
          document.body.style.overflow = '';
        }

        overlay.classList.add('transition-complete');

        if (lockedAppHeight) {
          overlay.classList.remove('height-locked');
          dialog.classList.remove('height-locked');
          document.documentElement.style.removeProperty('--modal-locked-height');
          lockedAppHeight = null;
        }

        if (overlay.parentNode === document.body) root.appendChild(overlay);
      }, TRANSITION_DURATION);
    }

    // ── Scroll lock (mobile) ───────────────────────────────────
    function lockBodyScroll() {
      bodyScrollY = window.scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${bodyScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (bodyScrollY) window.scrollTo(0, bodyScrollY);
      bodyScrollY = 0;
    }

    function bindTouchMoveLock() {
      if (touchMoveHandler) return;
      touchMoveHandler = (e) => {
        if (!e.target.closest('.dsearch__dropdown--modal')) e.preventDefault();
      };
      overlay.addEventListener('touchmove', touchMoveHandler, { passive: false });
    }

    function unbindTouchMoveLock() {
      if (touchMoveHandler) overlay.removeEventListener('touchmove', touchMoveHandler);
      touchMoveHandler = null;
    }

    // ── Overlay click handler ──────────────────────────────────
    function addOverlayClickHandler() {
      if (overlayClickHandler) return;
      overlayClickHandler = (e) => {
        if (!isModalOpen) return;
        if (dialog.contains(e.target)) {
          const link = e.target.closest('a');
          if (link) {
            e.preventDefault();
            sendSearchEventLog(link.innerText, true);
            closeModal();
            window.location.href = link.href;
          }
          return;
        }
        if (e.target === overlay) closeModal();
      };
      setTimeout(() => {
        if (overlay && overlayClickHandler) {
          overlay.addEventListener('click', overlayClickHandler);
        }
      }, OVERLAY_CLICK_DELAY);
    }

    function removeOverlayClickHandler() {
      if (overlayClickHandler && overlay) {
        overlay.removeEventListener('click', overlayClickHandler);
      }
      overlayClickHandler = null;
    }

    // ── Search input handler ───────────────────────────────────
    function onInput() {
      const q = modalInput.value.trim().toLowerCase();
      if (!q) {
        showPopular();
        return;
      }

      const { matchedCountries, matchedRegions } = filterDestinations(q);

      if (matchedCountries.length || matchedRegions.length) {
        renderResults(matchedCountries, matchedRegions);
      } else {
        showNoResults();
      }

      sendSearchEventLog(q);
    }

    // ── Filter ─────────────────────────────────────────────────
    function relevanceScore(search, term) {
      if (search === term) return 0;
      if (search.startsWith(term)) return 1;
      if (search.includes(' ' + term) || search.includes(',' + term)) return 2;
      if (search.includes(term)) return 3;
      return 4;
    }

    function filterDestinations(term) {
      const matchedCountries = countries
        .filter((c) => matchesQuery(c.search, term))
        .sort((a, b) => relevanceScore(a.search, term) - relevanceScore(b.search, term));

      const matchedRegions = regions
        .filter((r) => matchesQuery(r.search, term))
        .sort((a, b) => relevanceScore(a.search, term) - relevanceScore(b.search, term));

      // Surface parent regions for matched countries via each region's pipe-delimited country_list.
      const seenRegionIds = new Set(matchedRegions.map((r) => r.id));
      const matchedCountryIds = new Set(matchedCountries.map((c) => c.id));

      // Expand lookup set with search aliases — covers cases where the Shopify metaobject
      // handle and the country_list entry use different name forms (e.g. handle "turkey"
      // vs country_list "turkiye" when search_title includes both).
      matchedCountries.forEach((c) => {
        if (!c.search) return;
        c.search.split(',').forEach((alias) => {
          const h = alias.trim().replace(/\s+/g, '-');
          if (h) matchedCountryIds.add(h);
        });
      });

      regions.forEach((region) => {
        if (seenRegionIds.has(region.id) || !region.country_list) return;
        const handles = region.country_list
          .split('|')
          .map((h) => h.trim().toLowerCase().replace(/\s+/g, '-'));
        const matched = handles.some((h) => {
          if (matchedCountryIds.has(h)) return true;
          // Covers handles with abbreviation suffixes (e.g. "united-states-of-america-usa"
          // matching country_list entry "united-states-of-america").
          return [...matchedCountryIds].some(
            (id) => id.startsWith(h + '-') || h.startsWith(id + '-'),
          );
        });
        if (matched) {
          seenRegionIds.add(region.id);
          matchedRegions.push(region);
        }
      });

      // Global is the broadest option — always show it last after specific regions.
      matchedRegions.sort((a, b) => {
        const aGlobal = a.title.toLowerCase() === 'global';
        const bGlobal = b.title.toLowerCase() === 'global';
        return aGlobal === bGlobal ? 0 : aGlobal ? 1 : -1;
      });

      return { matchedCountries, matchedRegions };
    }

    /*
     * matchesQuery — four-tier matching:
     *
     * 1. Substring        — target.includes(term)
     * 2. Length guard     — no fuzzy for terms < 3 chars
     * 3. Subsequence+span — all term chars found in order within 1.5× span
     *                       catches "germny"→"germany", rejects "bali"→"balearic"
     * 4. Damerau-Levenshtein — two modes:
     *      Full word:  DL ≤ 1 (≤ 2 for terms ≥ 10 chars)
     *                  catches transpositions ("veitnam") and doubled chars ("fiij")
     *      Prefix:     compare term against same-length prefix of longer word;
     *                  fires when ≥ 60% of the word is typed, threshold = ceil(n/4)
     *                  catches mid-typing of misspelled words ("phillip"→philippines)
     */
    function matchesQuery(target, term) {
      if (!target || !term) return false;
      if (target.includes(term)) return true;
      if (term.length < 3) return false;

      let ti = 0,
        firstMatch = -1,
        lastMatch = -1;
      for (let i = 0; i < target.length && ti < term.length; i++) {
        if (target[i] === term[ti]) {
          if (firstMatch === -1) firstMatch = i;
          lastMatch = i;
          ti++;
        }
      }
      if (ti === term.length && lastMatch - firstMatch + 1 <= term.length * 1.5) return true;

      const maxDist = term.length >= 10 ? 2 : 1;
      return target.split(/[\s,()/]+/).some((word) => {
        if (word[0] !== term[0]) return false;
        if (
          Math.abs(word.length - term.length) <= maxDist &&
          damerauLevenshtein(term, word) <= maxDist
        )
          return true;
        // Prefix mode: user is mid-typing a misspelled word — compare against word's leading chars.
        if (term.length < word.length && term.length / word.length >= 0.6) {
          return damerauLevenshtein(term, word.slice(0, term.length)) <= Math.ceil(term.length / 4);
        }
        return false;
      });
    }

    // Restricted Damerau-Levenshtein: insertions, deletions, substitutions, adjacent transpositions.
    function damerauLevenshtein(a, b) {
      const m = a.length,
        n = b.length;
      const d = Array.from({ length: m + 1 }, (_, i) =>
        Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
      );
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
          if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
            d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
          }
        }
      }
      return d[m][n];
    }

    // ── Render results ─────────────────────────────────────────
    // Single <ul> keeps row-gap uniform across countries and regions.
    function renderResults(countryItems, regionItems) {
      if (!dropdown) return;

      const countryHtml = countryItems
        .map((c) => {
          const icon = c.icon_url
            ? `<img src="${c.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;">`
            : '';
          const url = resolveLocalizedUrl(c.url);
          const handle = c.handle || null;

          const titleAttr = (c.title || '').replace(/"/g, '&quot;');
          /* const priceStr = c.from_price
            ? formatPrice({
                amount: parseFloat(c.from_price),
                currencyCode: FROM_PRICE_CURRENCY,
                isFromPriceMetafield: true,
              })
            : ''; */
          return (
            `<li class="dsearch__item" data-item-title="${titleAttr}">` +
            `<a class="dsearch__link" href="${url}"${handle ? ` data-handle="${handle}"` : ''}>` +
            `<span class="dsearch__result-content">${icon}` +
            `<span class="dsearch__link-content">` +
            `<span class="dsearch__result-name">${c.title}</span>` +
            // `<span class="dsearch__price">${priceStr}</span>` +
            `</span></span><span class="dsearch__group-title">Country</span></a></li>`
          );
        })
        .join('');

      const regionHtml = regionItems
        .map((r) => {
          const icon = r.icon_url
            ? `<img src="${r.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;" onerror="this.style.display='none'">`
            : '';
          const url = resolveLocalizedUrl(r.url);
          const titleAttr = (r.title || '').replace(/"/g, '&quot;');
          const isGlobal = r.title.toLowerCase() === 'global';
          const badge = isGlobal
            ? '<span class="dsearch__badge dsearch__badge--new">Popular</span>'
            : '';
          const groupLabel = isGlobal
            ? '<span class="dsearch__group-title" style="background: none;">&nbsp;</span>'
            : '<span class="dsearch__group-title">Region</span>';
          const handle = r.handle || null;
          /* const priceStr = r.from_price
            ? formatPrice({
                amount: parseFloat(r.from_price),
                currencyCode: FROM_PRICE_CURRENCY,
                isFromPriceMetafield: true,
              })
            : ''; */

          return (
            `<li class="dsearch__item" data-item-title="${titleAttr}">` +
            `<a class="dsearch__link" href="${url}"${handle ? ` data-handle="${handle}"` : ''}>` +
            `<span class="dsearch__result-content">${icon}` +
            `<span class="dsearch__link-content">` +
            `<span class="dsearch__result-name">${r.display_title || (r.title ? capitalizeWords(r.title) : '')}</span>` +
            // `<span class="dsearch__price">${priceStr}</span>` +
            `</span>${badge}</span>${groupLabel}</a></li>`
          );
        })
        .join('');

      dropdown.innerHTML = `<ul class="dsearch__list">${countryHtml}${regionHtml}</ul>`;
      dropdown.hidden = false;
    }

    // ── Popular destinations ───────────────────────────────────
    function normalizeUrl(url) {
      return (url || '')
        .toLowerCase()
        .replace(/^https?:\/\/[^/]+/, '')
        .replace(/\?.*$/, '')
        .replace(/\/$/, '');
    }

    function resolveRankedPopular() {
      const code = (root.dataset.visitorCountry || '').toUpperCase();
      const raw = rankingsByCountry[code] || rankingsByCountry['default'];
      const unlimited = (unlimitedHandles[code] || '').split(',');

      if (!raw) return null;

      let entries;
      try {
        let parsed = JSON.parse(raw);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        entries = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return null;
      }

      entries.map((item, i) => {
        item.unlimitedHandle = unlimited[i] || null;
      });

      const tileByUrl = {};
      const tileByTitle = {};
      // countries/regions are the primary price source; popular is a fallback for any
      // destinations that lack pricing metafields on their collection products
      [...countries, ...regions, ...popular].forEach((t) => {
        const uk = normalizeUrl(t.url);
        const tk = (t.title || '').toLowerCase().trim();
        if (uk && (!tileByUrl[uk] || !tileByUrl[uk].from_price)) tileByUrl[uk] = t;
        if (tk && (!tileByTitle[tk] || !tileByTitle[tk].from_price)) tileByTitle[tk] = t;
      });

      return entries
        .map((item) => {
          const rank = Number(item.rank ?? item.position ?? item.order ?? 0);
          const url = item.link || item.url || item.href || item.path || '';
          const title =
            item.destination || item.title || item.name || item.label || item.country || '';
          const tile =
            tileByUrl[normalizeUrl(url)] || tileByTitle[title.toLowerCase().trim()] || {};
          return {
            rank,
            url,
            title,
            item_unlimited: item.unlimitedHandle || '',
            icon_url:
              item.icon_url || item.iconUrl || item.image_url || item.icon || tile.icon_url || '',
            type:
              item.destination_type ||
              item.destinationType ||
              item.type ||
              item.category ||
              tile.type ||
              'Country',
            from_price: tile.from_price || '',
            is_new: tile.is_new,
          };
        })
        .filter((item) => item.url && item.title)
        .sort((a, b) => a.rank - b.rank)
        .slice(0, 10);
    }

    function showPopular() {
      if (!dropdown) return;
      const tiles = resolveRankedPopular();
      if (!tiles || !tiles.length) return;

      let html =
        '<div class="dsearch__group-wrapper">' +
        '<div class="popular-destinations-title">Popular Destinations</div>' +
        '<ul class="dsearch__list">';

      html += tiles
        .map((dest) => {
          const icon = dest.icon_url
            ? `<img src="${dest.icon_url}" alt="" width="18" height="18" style="vertical-align:middle;object-fit:contain;" onerror="this.style.display='none'">`
            : '';
          const url = resolveLocalizedUrl(dest.item_unlimited || dest.url);
          const titleAttr = (dest.title || '').replace(/"/g, '&quot;');
          const isGlobal = (dest.title || '').toLowerCase() === 'global';
          const badge =
            isGlobal || dest.is_new === 'true' || dest.is_new === true
              ? '<span class="dsearch__badge dsearch__badge--new">Popular</span>'
              : '';
          const groupLabel = isGlobal
            ? '<span class="dsearch__group-title" style="background: none;">&nbsp;</span>'
            : `<span class="dsearch__group-title">${dest.type || 'Country'}</span>`;
          /* const priceStr = dest.from_price
            ? formatPrice({
                amount: parseFloat(dest.from_price),
                currencyCode: FROM_PRICE_CURRENCY,
                isFromPriceMetafield: true,
              })
            : ''; */
          return (
            `<li class="dsearch__item" data-item-title="${titleAttr}">` +
            `<a class="dsearch__link" href="${url}">` +
            `<span class="dsearch__result-content">${icon}` +
            `<span class="dsearch__link-content">` +
            `<span class="dsearch__result-name">${dest.title}</span>` +
            // `<span class="dsearch__price">${priceStr}</span>` +
            `</span>${badge}</span>${groupLabel}</a></li>`
          );
        })
        .join('');

      html += '</ul></div>';
      dropdown.innerHTML = html;
      dropdown.hidden = false;
    }

    // ── UI state ───────────────────────────────────────────────
    function showNoResults() {
      if (!dropdown) return;
      dropdown.innerHTML =
        '<div class="dsearch__group-title no-results">No results. Try searching another country or region.</div>';
      dropdown.hidden = false;
    }

    // ── Analytics ──────────────────────────────────────────────
    // Clicks bypass the debounce (0 ms); search terms fire after 1 s of inactivity.
    function sendSearchEventLog(searchTerm, clicked = false) {
      if (!searchTerm) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(
        () => {
          const endpoint = 'https://portal.simify.com/api/v2/webhooks/customEvent';
          const country = (typeof Shopify !== 'undefined' && Shopify.country) || '';
          const payload = JSON.stringify({
            eventSource: 'search_input',
            eventType: 'search_term',
            additionalInfo: (clicked ? `${searchTerm};clicked` : searchTerm) + `;${country}`,
          });
          try {
            if (navigator.sendBeacon) {
              navigator.sendBeacon(endpoint, payload);
            } else {
              fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true,
              });
            }
          } catch (err) {
            /* beacon is non-critical */
          }
        },
        clicked ? 0 : 1000,
      );
    }

    // ── Utilities ──────────────────────────────────────────────

    /* price display commented out — VWO winning test; restore block to re-enable
    function formatPrice(price) {
      if (!price || price.amount == null) return '';
      const amount = typeof price.amount === 'number' ? price.amount : parseFloat(price.amount);
      const fromCurrency = price.currencyCode || getDisplayCurrency();
      const displayCurrency = getDisplayCurrency();
      const displayAmount =
        fromCurrency === displayCurrency ? amount : convertToDisplayCurrency(amount, fromCurrency);
      const truncated = price.isFromPriceMetafield
        ? Math.round(displayAmount * 100) / 100
        : Math.floor(displayAmount * 100 + 1e-6) / 100;
      return (
        'From ' +
        new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: displayCurrency,
          currencyDisplay: 'narrowSymbol',
        })
          .format(truncated)
          .replace(/\.00$/, '') +
        '/day'
      );
    }

    // Active display currency — priority: cart currency → currency selector → AUD fallback.
    function getDisplayCurrency() {
      const parseCode = (t) => (t && (t = t.trim().match(/\b([A-Z]{3})\b/)) ? t[1] : null);
      const fromCart = parseCode(window.cartCurrency);
      if (fromCart) return fromCart;
      const el = document.querySelector(
        'currency-selector .cs-currency.is-active, currency-selector .cs-currency[aria-current="true"], currency-selector .cs-trigger span',
      );
      return (el && parseCode(el.textContent)) || 'AUD';
    }

    function convertToDisplayCurrency(amount, fromCurrency) {
      const displayCurrency = getDisplayCurrency();
      if (fromCurrency === displayCurrency || !amount || typeof amount !== 'number') return amount;
      const table = window.exchangeTable;
      if (!table || typeof table !== 'object') return amount;
      const rateFrom = table[fromCurrency];
      const rateTo = table[displayCurrency];
      if (!rateFrom || !rateTo) return amount;
      return (amount / rateFrom) * rateTo;
    }
    */

    // Prepend active market prefix to /collections or /products URLs.
    function resolveLocalizedUrl(rawUrl) {
      if (!rawUrl) return rawUrl;
      try {
        const url = rawUrl.startsWith('http')
          ? new URL(rawUrl)
          : new URL(rawUrl, window.location.origin);
        const path = url.pathname;
        const parts = window.location.pathname.split('/').filter(Boolean);
        const isMarket = /^[a-z]{2}-[a-z]{2}$/i.test(parts[0]);
        const marketPrefix = isMarket ? `/${parts[0].toLowerCase()}` : '';
        const cleanPath = path.replace(/^\/[a-z]{2}-[a-z]{2}(?=\/)/i, '');
        if (!cleanPath.startsWith('/products') && !cleanPath.startsWith('/collections'))
          return rawUrl;
        return `${marketPrefix}${cleanPath}${url.search}${url.hash}`;
      } catch (e) {
        return rawUrl;
      }
    }
  } // end initModal
})();
